import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Settings, 
  ChevronLeft, BookOpen, Clock, Calendar, CheckCircle2,
  SkipForward, SkipBack, FastForward, PlayCircle, Video as VideoIcon
} from "lucide-react";
import { cn, getFileUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  useGetVideosQuery, 
  videoApi 
} from "@/redux/slices/videoApi";
import { format } from "date-fns";

export default function VideoDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Use the specific video from the list query for now (or a getSingle query if available)
  const { data: videosData, isLoading } = useGetVideosQuery();
  const video = videosData?.data?.find(v => v.id === id);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onTimeUpdate = () => {
      setCurrentTime(v.currentTime);
      setProgress((v.currentTime / v.duration) * 100);
    };
    const onLoadedMetadata = () => setDuration(v.duration);

    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [video]);

  const togglePlay = () => {
    if (videoRef.current?.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (val: number[]) => {
    if (videoRef.current) {
      const time = (val[0] / 100) * duration;
      videoRef.current.currentTime = time;
      setProgress(val[0]);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  if (isLoading) return <div className="flex h-[80vh] items-center justify-center"><RotateCw className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!video) return <div className="p-8 text-center">Video not found.</div>;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <div className="flex items-center gap-4 border-b bg-card px-6 py-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{video.title}</h1>
          <p className="text-xs text-muted-foreground">{video.chapter?.name} • {video.subject?.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-3">
        {/* Main Player Section */}
        <div className="lg:col-span-2 space-y-6">
          <div 
            className="group relative aspect-video overflow-hidden rounded-2xl bg-black shadow-2xl transition-all"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            {video.platform === 'youtube' ? (
               <iframe
                src={`https://www.youtube.com/embed/${video.url.split('v=')[1]?.split('&')[0] || video.url.split('/').pop()}`}
                className="h-full w-full"
                allowFullScreen
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={getFileUrl(video.url)}
                  className="h-full w-full"
                  onClick={togglePlay}
                />
                
                {/* Custom Controls Overlay */}
                <div className={cn(
                  "absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-transparent to-transparent p-4 transition-opacity duration-300",
                  showControls ? "opacity-100" : "opacity-0"
                )}>
                  {/* Progress Bar */}
                  <div className="mb-4 group/progress cursor-pointer">
                    <Slider 
                      value={[progress]} 
                      max={100} 
                      step={0.1} 
                      onValueChange={handleSeek}
                      className="cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={togglePlay}>
                        {isPlaying ? <Pause className="h-6 w-6 fill-current" /> : <Play className="h-6 w-6 fill-current" />}
                      </Button>
                      <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                        <span>{formatTime(currentTime)}</span>
                        <span className="text-white/40">/</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 px-2">
                        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setIsMuted(!isMuted)}>
                          {isMuted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                        </Button>
                        <Slider 
                          value={[isMuted ? 0 : volume * 100]} 
                          max={100} 
                          className="w-24"
                          onValueChange={(v) => {
                            setVolume(v[0] / 100);
                            if (videoRef.current) videoRef.current.volume = v[0] / 100;
                            setIsMuted(v[0] === 0);
                          }}
                        />
                      </div>
                      <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
                        <Settings className="h-5 w-5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => videoRef.current?.requestFullscreen()}>
                        <Maximize className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Center Play Button (Large) */}
                {!isPlaying && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer group/play-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay();
                    }}
                  >
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/90 text-white shadow-2xl backdrop-blur-sm transition-all duration-300 group-hover/play-btn:scale-110 group-hover/play-btn:bg-primary">
                      <Play className="h-10 w-10 fill-current translate-x-0.5" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Info Card */}
          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <Badge variant="outline" className="mb-2 bg-primary/5 text-primary border-primary/20">
                  {video.videoType === 'local' ? 'Server Hosted' : 'External Link'}
                </Badge>
                <h2 className="text-2xl font-bold tracking-tight">{video.title}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Mark as Completed
                </Button>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <BookOpen className="h-3 w-3" /> Subject
                </p>
                <p className="text-sm font-medium">{video.subject?.name}</p>
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" /> Duration
                </p>
                <p className="text-sm font-medium">{video.duration || 'Not specified'}</p>
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" /> Uploaded On
                </p>
                <p className="text-sm font-medium">{format(new Date(video.createdAt), "dd MMM yyyy")}</p>
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <VideoIcon className="h-3 w-3" /> Quality
                </p>
                <p className="text-sm font-medium">1080p Full HD</p>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <h3 className="font-semibold">Description</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {video.description || "No description provided for this video lecture."}
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar Section */}
        <div className="space-y-6">
          <div className="rounded-2xl border bg-card overflow-hidden shadow-sm">
            <div className="bg-muted/50 p-4 font-semibold flex items-center justify-between">
              <span>Chapter Content</span>
              <Badge variant="secondary">{videosData?.total || 0} Videos</Badge>
            </div>
            <div className="divide-y">
              {videosData?.data?.filter(v => v.chapterId === video.chapterId).map((v, i) => (
                <Link 
                  key={v.id} 
                  to={`/videos/${v.id}`}
                  className={cn(
                    "flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors",
                    v.id === id && "bg-primary/5 border-l-4 border-primary"
                  )}
                >
                  <div className="relative flex h-12 w-20 shrink-0 items-center justify-center rounded-md bg-black/10 overflow-hidden">
                    <PlayCircle className={cn("h-5 w-5", v.id === id ? "text-primary" : "text-muted-foreground")} />
                    {v.id === id && <div className="absolute inset-0 bg-primary/10" />}
                  </div>
                  <div className="min-w-0">
                    <p className={cn("text-xs font-medium truncate", v.id === id && "text-primary")}>{v.title}</p>
                    <p className="text-[10px] text-muted-foreground">{v.duration || 'Duration unknown'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RotateCw(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  )
}
